/**
 * Send a ZPL label to a Zebra printer through the Android shell's print bridge
 * (window.AndroidPrinterBridge, APK 1.3.0+). The shell opens the printer's raw print port on the
 * warehouse network and replies via window.PrinterBridge.onResult(requestId, ok, message).
 */

const TIMEOUT_MS = 20_000;

interface Pending {
  resolve: () => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}
const pending = new Map<string, Pending>();
let seq = 0;

/** True when the app is running in a scanner shell that can print (not a plain browser or older APK). */
export function canPrintOnDevice(): boolean {
  return typeof window !== "undefined" && typeof window.AndroidPrinterBridge?.printZpl === "function";
}

function installResultHandler(): void {
  window.PrinterBridge ??= {
    onResult(requestId, ok, message) {
      const p = pending.get(requestId);
      if (!p) return;
      pending.delete(requestId);
      clearTimeout(p.timer);
      if (ok) p.resolve();
      else p.reject(new Error(`Couldn't print (${message || "no reason given"}). Check the printer is on and connected to the network.`));
    },
  };
}

/** Print `zpl` on the printer at `host:port`. Resolves once the label has been sent. */
export function printZplOnDevice(host: string, port: number, zpl: string): Promise<void> {
  const bridge = window.AndroidPrinterBridge;
  if (!bridge) return Promise.reject(new Error("Printing works in the scanner app only."));
  installResultHandler();
  const requestId = `print-${Date.now()}-${++seq}`;
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(requestId);
      reject(new Error("No answer from the printer — check it's on and connected to the network."));
    }, TIMEOUT_MS);
    pending.set(requestId, { resolve, reject, timer });
    bridge.printZpl(requestId, host, port, zpl);
  });
}
